
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Rows4, Users, OctagonAlert } from 'lucide-react';
import { getProducts, getAllProducts, acceptProduct, rejectProduct } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';
import { useGlobalState } from '../../utils/GlobalState';

const BackendUrl = process.env.REACT_APP_BACKEND_URL;

const ProductInfoInput = ({ productDetails }) => {
  const navigate = useNavigate();
  const { state, updateState } = useGlobalState();
  // const [customRatio, setCustomRatio] = useState('');
  // const [customRatios, setCustomRatios] = useState([]);
  const [products, setProducts] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadDetails, setShowUploadDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [previewType, setPreviewType] = useState('');
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [totalUploads, setTotalUploads] = useState(0);
  const [prevTotalUploads, setPrevTotalUploads] = useState(0);
  const [sliderBg, setSliderBg] = useState('linear-gradient(to right, #d1d5db 0%, #d1d5db 100%)');
  const [myUploads, setMyUploads] = useState(0);
  const [myAccepted, setMyAccepted] = useState(0);
  const [totalAccepted, setTotalAccepted] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [submissionBlocked, setSubmissionBlocked] = useState(false);
  const [screenType, setScreenType] = useState(null);
  const [selectedInch, setSelectedInch] = useState('');

  const decoded = decodedToken();
  const email = decoded?.email || '';
  const companyInfo = decoded?.companyInfo || 'Unknown'
  const maxUploads = 2000;

  const defaultState = {
    productType: "TV",
    modelName: "",
    hasBezel: null,
    leakageRation: null,
    dimensions: {
      width: { input: "", whole: 0, decimal: 0 },
      height: { input: "", whole: 0, decimal: 0 }
    },
    screenType: null,
    inchSize: ""
  };

  useEffect(() => {
    if (!state.formData) {
      updateState({ formData: [] });
    }
  }, [state.formData, updateState]);

  const latestFormData = state.formData && state.formData.length > 0 ? state.formData[state.formData.length - 1] : null;

  const [productType, setProductType] = useState(latestFormData?.productType || defaultState.productType);
  const [modelName, setModelName] = useState(latestFormData?.modelName || defaultState.modelName);
  const [hasBezel, setHasBezel] = useState(
    latestFormData?.bezel === "yes" ? true : latestFormData?.bezel === "no" ? false : defaultState.hasBezel
  );
  // const [leakageRation, setLeakageRation] = useState(latestFormData?.lightLeakageRatioCriteria || defaultState.leakageRation);
  const [dimensions, setDimensions] = useState(
    latestFormData?.dimensions
      ? {
          width: {
            input: latestFormData.dimensions.width || "",
            whole: parseInt(latestFormData.dimensions.width?.split('.')[0]) || 0,
            decimal: parseInt(latestFormData.dimensions.width?.split('.')[1]?.[0]) || 0
          },
          height: {
            input: latestFormData.dimensions.height || "",
            whole: parseInt(latestFormData.dimensions.height?.split('.')[0]) || 0,
            decimal: parseInt(latestFormData.dimensions.height?.split('.')[1]?.[0]) || 0
          }
        }
      : defaultState.dimensions
  );
  const [screenTypeState, setScreenTypeState] = useState(latestFormData?.screenType || defaultState.screenType);
  const [selectedInchState, setSelectedInchState] = useState(latestFormData?.inchSize || defaultState.inchSize);

  useEffect(() => {
    const decode = decodedToken();
    setIsAdmin(decode?.userType === "powerAdmin");

    const fetchInitialUploadCount = async () => {
      try {
        const allProducts = await getAllProducts();
        const totalRecords = Math.min(allProducts.data.length, maxUploads);
        setTotalUploads(totalRecords);
        setTotalAccepted(allProducts.data.filter(p => p.action === 'accepted').length);
        setTotalPending(allProducts.data.filter(p => p.action === null).length);
        setSubmissionBlocked(totalRecords >= maxUploads);

        const myProducts = await getProducts(email);
        setMyUploads(myProducts.data.length);
        setMyAccepted(myProducts.data.filter(p => p.action === 'accepted').length);
      } catch (error) {
        console.error('Error fetching initial upload count:', error);
      }
    };
    fetchInitialUploadCount();
  }, [email]);

  // const predefinedRatios = {
  //   TV: [1.6, 1.8],
  //   PD: [1.6, 1.8],
  //   MNT: [1.0]
  // };

  useEffect(() => {
    if (productType === 'MNT') {
      // setLeakageRation(1.0);
      // setCustomRatios([]);
      // setCustomRatio('');
      setScreenType(null);
      setSelectedInch('');
      setDimensions(defaultState.dimensions);
    } else {
      // setLeakageRation(null);
      // setCustomRatios([]);
      // setCustomRatio('');
      setScreenType(null);
      setSelectedInch('');
      setDimensions(defaultState.dimensions);
    }
  }, [productType]);

  useEffect(() => {
    const percentage = (totalUploads / maxUploads) * 100;
    let gradient;
    if (totalUploads > prevTotalUploads) {
      gradient = `linear-gradient(to right, #3b82f6 ${percentage}%, #d1d5db ${percentage}%)`;
    } else if (totalUploads < prevTotalUploads) {
      gradient = `linear-gradient(to right, #d1d5db ${percentage}%, #3b82f6 ${percentage}%)`;
    } else {
      gradient = totalUploads > 0
        ? `linear-gradient(to right, #3b82f6 ${percentage}%, #d1d5db ${percentage}%)`
        : `linear-gradient(to right, #d1d5db 0%, #d1d5db 100%)`;
    }
    setSliderBg(gradient);
    setPrevTotalUploads(totalUploads);
  }, [totalUploads, prevTotalUploads]);

  const handleDimensionChange = (dimension, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      let whole = 0;
      let decimal = 0;
      if (value !== '') {
        const [w, d = '0'] = value.split('.');
        whole = parseInt(w) || 0;
        decimal = parseInt(d[0] || '0') || 0;
      }
      setDimensions(prev => ({
        ...prev,
        [dimension]: {
          input: value,
          whole,
          decimal
        }
      }));
      setSelectedInch('');
    }
  };

  // const handleCustomRatioChange = (e) => {
  //   const value = e.target.value;
  //   if (/^\d*\.?\d*$/.test(value) && value <= 10) {
  //     setCustomRatio(value);
  //   }
  // };

  // const addCustomRatio = () => {
  //   if (customRatio && !customRatios.includes(parseFloat(customRatio)) && parseFloat(customRatio) > 0) {
  //     const newRatio = parseFloat(customRatio);
  //     setCustomRatios([...customRatios, newRatio]);
  //     setLeakageRation(newRatio);
  //     setCustomRatio('');
  //   }
  // };

  const isFormValid = () => {
    if (!modelName || modelName.trim() === '') {
      return false;
    }
    const width = parseFloat(`${dimensions.width.whole}.${dimensions.width.decimal}`);
    const height = parseFloat(`${dimensions.height.whole}.${dimensions.height.decimal}`);
    if (isNaN(width) || width <= 0 || isNaN(height) || height <= 0) {
      return false;
    }
    if (productType === 'TV' || productType === 'MNT') {
      if (hasBezel === null) {
        return false;
      }
      if (productType === 'MNT' && screenType === null) {
        return false;
      }
    }
    return true;
  };

const handleSave = () => {
  // if (submissionBlocked || totalUploads >= maxUploads) {
  //   Swal.fire({
  //     title: 'Upload Limit Reached',
  //     text: 'The maximum upload count of 500 has been filled. No further submissions are allowed.',
  //     icon: 'warning',
  //     confirmButtonText: 'OK',
  //   });
  //   return;
  // }

  if (!isFormValid()) {
    Swal.fire({
      title: 'Invalid Input',
      text: 'Please fill all required fields correctly.',
      icon: 'error',
      confirmButtonText: 'OK',
    });
    return;
  }

  const productData = {
    productType,
    modelName,
    bezel: productType !== 'PD' ? (hasBezel ? 'yes' : 'no') : undefined,
    dimensions: {
      width: `${dimensions.width.whole}.${dimensions.width.decimal}`,
      height: `${dimensions.height.whole}.${dimensions.height.decimal}`
    },
    screenType: productType === 'MNT' ? screenType : undefined,
    inchSize: selectedInch || undefined,
    companyInfo // Add companyInfo to productData
  };

  const updatedFormData = [...(state.formData || []), productData];
  updateState({ formData: updatedFormData });
  navigate('/performance-recording', { state: { productData } });
};
  const handleBack = () => {
    updateState({ formData: [] });
    navigate("/dashboard");
  };

  const fetchMyProducts = async () => {
    setIsLoading(true);
    setPreviewType('my');
    setProducts(null);
    try {
      if (!email || typeof email !== 'string' || !email.includes('@')) {
        throw new Error('Invalid or missing email address');
      }

      const data = await getProducts(email);
      const allProducts = Array.isArray(data?.data) ? data.data : [];
      const acceptedProducts = allProducts.filter(product => product.action === 'accepted');

      setMyUploads(allProducts.length);
      setMyAccepted(acceptedProducts.length);

      const allRecords = await getAllProducts();
      const totalRecords = Math.min(Array.isArray(allRecords?.data) ? allRecords.data.length : 0, maxUploads);
      setTotalUploads(totalRecords);
      setTotalAccepted(allRecords.data?.filter(p => p.action === 'accepted')?.length || 0);
      setTotalPending(allRecords.data?.filter(p => p.action === null)?.length || 0);
      setSubmissionBlocked(totalRecords >= maxUploads);

      setProducts(acceptedProducts);
      const initialLoadingState = {};
      acceptedProducts.forEach(product => {
        initialLoadingState[`${product._id}_white`] = true;
        initialLoadingState[`${product._id}_black`] = true;
      });
      setImageLoading(initialLoadingState);
      setShowPreview(true);

      if (allProducts.length === 0) {
        Swal.fire({
          title: 'No Uploads',
          text: 'Zero uploads from this mail.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      } else if (acceptedProducts.length === 0) {
        Swal.fire({
          title: 'No Accepted Products',
          text: 'No accepted products found for your account.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.warn('Failed to fetch products:', {
        message: error.message,
        stack: error.stack,
        email
      });
      Swal.fire({
        title: 'No Uploads',
        text: 'Zero uploads from this account for Good Performance.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    setIsLoading(true);
    setPreviewType('all');
    setProducts(null);
    try {
      const data = await getAllProducts();
      const totalRecords = Math.min(data.data.length, maxUploads);
      const pendingProducts = data.data
        .filter(product => product.action === null)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setTotalUploads(totalRecords);
      setTotalAccepted(data.data.filter(p => p.action === 'accepted').length);
      setTotalPending(pendingProducts.length);
      setSubmissionBlocked(totalRecords >= maxUploads);

      setProducts(pendingProducts);
      const initialLoadingState = {};
      pendingProducts.forEach(product => {
        initialLoadingState[`${product._id}_white`] = true;
        initialLoadingState[`${product._id}_black`] = true;
      });
      setImageLoading(initialLoadingState);
      setShowPreview(true);

      if (!pendingProducts.length) {
        Swal.fire({
          title: 'Info',
          text: 'No pending products awaiting approval.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error fetching all products:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to fetch products',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadDetailsClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowUploadDetails(true);
  };

  const handleAcceptProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You are about to accept this product record.",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, accept it!'
      });

      if (result.isConfirmed) {
        await acceptProduct(productId);
        Swal.fire({
          title: 'Accepted!',
          text: 'The product record has been accepted.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        if (previewType === 'all') {
          await fetchAllProducts();
        } else {
          await fetchMyProducts();
        }
      }
    } catch (error) {
      console.error('Error accepting product:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to accept product record',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleRejectProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You are about to reject and permanently delete this product record!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, reject it!'
      });

      if (result.isConfirmed) {
        await rejectProduct(productId);
        Swal.fire({
          title: 'Rejected!',
          text: 'The product record has been rejected and deleted.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        if (previewType === 'all') {
          await fetchAllProducts();
        } else {
          await fetchMyProducts();
        }
      }
    } catch (error) {
      console.error('Error rejecting product:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to reject product record',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleImageLoad = (key) => {
    setImageLoading(prev => ({
      ...(prev ?? {}),
      [key]: false
    }));
  };

  const handleImageClick = (imageUrl) => {
    setFullScreenImage(imageUrl);
  };

  const closeFullScreen = () => {
    setFullScreenImage(null);
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center mb-6">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg sm:text-xl font-medium whitespace-nowrap">Product Info Input</h1>
        <div className="ml-auto flex space-x-2">
          <button 
            className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 cursor-pointer bg-gray-700 hover:bg-gray-600"
            onClick={fetchMyProducts}
            disabled={isLoading}
            title="View My Accepted Products"
          >
            {isLoading && previewType === 'my' ? 'Loading...' : <Rows4 className="w-5 h-5" />}
          </button>
          {isAdmin && (
            <button 
              className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 cursor-pointer bg-gray-700 hover:bg-gray-600"
              onClick={fetchAllProducts}
              disabled={isLoading}
              title="View Pending Products (Admin Only)"
            >
              {isLoading && previewType === 'all' ? 'Loading...' : <Users className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {fullScreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[60]">
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={fullScreenImage}
              alt="Full Screen Preview"
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
            <button
              onClick={closeFullScreen}
              className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {showUploadDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[70] p-4">
          <div className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">Upload Details</h2>
              <button
                onClick={() => setShowUploadDetails(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="px-6 py-4 border-b border-gray-700">
              <div className="grid grid-cols-2 gap-4 text-gray-200">
                <div>
                  <div>Max Uploads: {maxUploads}</div>
                  <input
                    type="range"
                    min="0"
                    max={maxUploads}
                    value={totalUploads}
                    disabled
                    style={{ background: sliderBg }}
                    className="mt-2 w-full h-2 rounded-full outline-none opacity-70 transition-opacity duration-200 appearance-none disabled:opacity-50 disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-default [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-default [&::-moz-range-thumb]:border-none"
                  />
                </div>
                <div>Total Uploads: {totalUploads}</div>
                {previewType === 'my' ? (
                  <>
                    <div>My Uploads: {myUploads}</div>
                    <div>My Accepted Products: {myAccepted}</div>
                    <div>Total Accepted Products: {totalAccepted}</div>
                  </>
                ) : (
                  <>
                    <div>Total Accepted Products: {totalAccepted}</div>
                    <div>Total Pending Products: {totalPending}</div>
                  </>
                )}
              </div>
              {submissionBlocked && (
                <div className="mt-4 text-red-500">
                  Maximum upload limit of 500 reached. No further submissions allowed.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPreview && products && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">
                {previewType === 'all' ? "Pending Products (Admin View)" : "My Accepted Products"}
              </h2>
              <div className="flex items-center space-x-2">
                <div className="relative group">
                  <OctagonAlert
                    className="w-6 h-6 text-white cursor-pointer"
                    onClick={handleUploadDetailsClick}
                  />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-700 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-opacity duration-200">
                    Upload Details
                  </span>
                </div>
                <button 
                  onClick={() => setShowPreview(false)} 
                  className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 pb-20">
                {products.length === 0 ? (
                  <p className="text-gray-400 text-center">
                    {previewType === 'all' ? 'No pending products awaiting approval.' : 'No accepted products found.'}
                  </p>
                ) : (
                  <div className="grid gap-6">
                    {products.map((product) => (
                      <div key={product._id} className="bg-gray-900 rounded-lg p-5 hover:shadow-lg transition-shadow duration-200 border border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">Product Information</h3>
                            <div className="space-y-2 text-gray-200">
                              <div className="flex justify-between"><span className="text-gray-400">Type:</span><span>{product.productInfo.productType}</span></div>
                              <div className="flex justify-between"><span className="text-gray-400">Model:</span><span>{product.productInfo.modelName}</span></div>
                              {product.productInfo.bezel && (
                                <div className="flex justify-between"><span className="text-gray-400">Bezel:</span><span>{product.productInfo.bezel}</span></div>
                              )}
                              {/* <div className="flex justify-between"><span className="text-gray-400">Leakage Ratio:</span><span>{product.productInfo.lightLeakageRatioCriteria}</span></div> */}
                              <div className="flex justify-between"><span className="text-gray-400">Dimensions:</span><span>{product.productInfo.dimensions.width} × {product.productInfo.dimensions.height} mm</span></div>
                              {(product.productInfo.productType === 'TV' || product.productInfo.productType === 'MNT') && product.productInfo.inchSize && (
                                <div className="flex justify-between"><span className="text-gray-400">Inch Size:</span><span>{product.productInfo.inchSize}"</span></div>
                              )}
                              {product.productInfo.productType === 'MNT' && product.productInfo.screenType && (
                                <div className="flex justify-between"><span className="text-gray-400">Screen Type:</span><span>{product.productInfo.screenType}</span></div>
                              )}
                              {(isAdmin && previewType === 'all') && (
                                <div className="flex justify-between"><span className="text-gray-400">Company:</span><span>{product.productInfo.companyInfo}</span></div>
                              )}
                              {(isAdmin && previewType === 'all') && (
                                <div className="flex justify-between"><span className="text-gray-400">Uploaded by:</span><span>{product.email}</span></div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-400">Status:</span>
                                <span
                                  className={
                                    product.action === 'accepted'
                                      ? 'text-green-400'
                                      : product.action === 'rejected'
                                      ? 'text-red-400'
                                      : 'text-yellow-400'
                                  }
                                >
                                  {product.action === 'accepted'
                                    ? 'Accepted'
                                    : product.action === 'rejected'
                                    ? 'Rejected'
                                    : 'Pending'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">Performance Recording</h3>
                            <div className="grid grid-cols-1 gap-4">
                              <div className="space-y-2">
                                <p className="text-gray-400 font-medium">White Pattern</p>
                                <div className="relative group h-48 cursor-pointer" onClick={() => handleImageClick(`${product.performanceRecording.whitePattern.imageUrl}`)}>
                                  {imageLoading[`${product._id}_white`] && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-lg">
                                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                  )}
                                  <img
                                    src={`${product.performanceRecording.whitePattern.imageUrl}`}
                                    alt="White Pattern"
                                    className={`w-full h-full object-cover rounded-lg transition-transform duration-200 group-hover:scale-105 ${imageLoading[`${product._id}_white`] ? 'opacity-0' : 'opacity-100'}`}
                                    onLoad={() => handleImageLoad(`${product._id}_white`)}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-gray-400 font-medium">Black Pattern</p>
                                <div className="relative group h-48 cursor-pointer" onClick={() => handleImageClick(`${product.performanceRecording.blackPattern.imageUrl}`)}>
                                  {imageLoading[`${product._id}_black`] && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-lg">
                                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                  )}
                                  <img
                                    src={`${product.performanceRecording.blackPattern.imageUrl}`}
                                    alt="Black Pattern"
                                    className={`w-full h-full object-cover rounded-lg transition-transform duration-200 group-hover:scale-105 ${imageLoading[`${product._id}_black`] ? 'opacity-0' : 'opacity-100'}`}
                                    onLoad={() => handleImageLoad(`${product._id}_black`)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {isAdmin && previewType === 'all' && product.action === null && (
                          <div className="mt-4 flex justify-end space-x-4">
                            <button
                              onClick={() => handleAcceptProduct(product._id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleRejectProduct(product._id)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block mb-3">Product Type</label>
          <div className="relative">
            <div className="flex w-full">
              {['TV', 'MNT', 'PD'].map((type) => (
                <button
                  key={type}
                  onClick={() => setProductType(type)}
                  className={`flex-1 py-2 text-center relative ${
                    productType === type ? 'text-blue-500' : 'text-gray-400'
                  }`}
                >
                  {type}
                  {productType === type && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500" />
                  )}
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 w-full h-px bg-gray-800" />
          </div>
        </div>

        <div>
          <label className="block mb-3">Model Name</label>
          <input
            type="text"
            placeholder="Enter model name"
            value={modelName}
            onChange={(e) => setModelName(e.target.value.slice(0, 25))}
            maxLength={25}
            className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400"
          />
        </div>

        {productType !== 'PD' && (
          <div>
            <label className="block mb-3">Bezel</label>
            <div className="flex space-x-4">
              <button
                onClick={() => setHasBezel(true)}
                className={`px-6 py-2 rounded-full ${
                  hasBezel === true ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                Yes
              </button>
              <button
                onClick={() => setHasBezel(false)}
                className={`px-6 py-2 rounded-full ${
                  hasBezel === false ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Commented out Light Leakage Ratio Criteria UI and functionality */}
        {/* <div>
          <label className="block mb-3">Light Leakage Ratio Criteria</label>
          <div className="flex flex-wrap gap-4">
            {predefinedRatios[productType].map((ratio) => (
              <button
                key={ratio}
                onClick={() => setLeakageRation(ratio)}
                className={`px-6 py-2 rounded-full ${
                  leakageRation === ratio ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {ratio === 1.0 ? 'No Leakage (1.0)' : ratio}
              </button>
            ))}
            {productType === 'MNT' && [1.6, 1.8].map((ratio) => (
              <button
                key={ratio}
                className="px-6 py-2 rounded-full bg-gray-600 text-gray-400 cursor-not-allowed"
                disabled
              >
                {ratio}
              </button>
            ))}
            {customRatios.map((ratio) => (
              <button
                key={ratio}
                onClick={() => setLeakageRation(ratio)}
                className={`px-6 py-2 rounded-full relative ${
                  leakageRation === ratio ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {ratio}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomRatios(customRatios.filter((r) => r !== ratio));
                    if (leakageRation === ratio) setLeakageRation(null);
                  }}
                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </button>
            ))}
            {productType !== 'MNT' && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Custom (e.g., 2.0)"
                  value={customRatio}
                  onChange={handleCustomRatioChange}
                  className="bg-gray-800 rounded-lg px-4 py-2 text-white placeholder-gray-400 w-32"
                />
                <button
                  onClick={addCustomRatio}
                  className={`p-2 rounded-full ${
                    customRatio && !customRatios.includes(parseFloat(customRatio)) && parseFloat(customRatio) > 0
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-600 cursor-not-allowed'
                  }`}
                  disabled={!customRatio || customRatios.includes(parseFloat(customRatio)) || parseFloat(customRatio) <= 0}
                >
                  <Plus className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div> */}

        {(productType === 'TV' || productType === 'MNT') && (
          <div>
            {productType === 'MNT' && (
              <div className="mb-6">
                <label className="block mb-1">Screen Type</label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setScreenType(screenType === 'Flat' ? null : 'Flat')}
                    className={`px-6 py-2 rounded-full ${
                      screenType === 'Flat' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    Flat
                  </button>
                  <button
                    onClick={() => setScreenType(screenType === 'Curved' ? null : 'Curved')}
                    className={`px-6 py-2 rounded-full ${
                      screenType === 'Curved' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    Curved
                  </button>
                </div>
              </div>
            )}
            <div className="mb-6">
              <label className="block mb-2">Inch Size Selection</label>
              <div className="relative">
                <select
                  className="dropdown block w-full bg-gray-800 text-white rounded-lg px-4 py-3"
                  onChange={(e) => {
                    const selectedInch = e.target.value;
                    const inchToDimensions =
                      productType === 'TV'
                      ? {
                        '32': { width: '708', height: '398' },
                        '40': { width: '885', height: '498' },
                        '42': { width: '933', height: '535' },
                        '43': { width: '952', height: '535' },
                        '48': { width: '1069', height: '612' },
                        '50': { width: '1107', height: '623' },
                        '55': { width: '1218', height: '685' },
                        '58': { width: '1285', height: '723' },
                        '65': { width: '1439', height: '810' },
                        '70': { width: '1562', height: '860' },
                        '75': { width: '1660', height: '934' },
                        '77': { width: '1721', height: '985' },
                        '85': { width: '1882', height: '1058' },
                        '86': { width: '1905', height: '1072' },
                        '98': { width: '2170', height: '1220' },
                        '100': { width: '2214', height: '1246' },
                      }
                    : {
                        '16': screenType === 'Flat' ? { width: '364', height: '230' } : { width: '', height: '' },
                        '22': screenType === 'Flat' ? { width: '492', height: '290' } : { width: '', height: '' },
                        '24': screenType === 'Flat' ? { width: '543', height: '320' } : { width: '537', height: '323' },
                        '25': screenType === 'Flat' ? { width: '560', height: '324' } : { width: '', height: '' },
                        '27': screenType === 'Flat' ? { width: '614', height: '368' } : { width: '612', height: '368' },
                        '30': screenType === 'Flat' ? { width: '', height: '' } : { width: '701', height: '322' },
                        '32': screenType === 'Flat' ? { width: '715', height: '421' } : { width: '700', height: '424' },
                        '34': screenType === 'Flat' ? { width: '817', height: '371' } : { width: '810', height: '366' },
                        '45': screenType === 'Flat' ? { width: '', height: '' } : { width: '1085', height: '337' },
                        '49': screenType === 'Flat' ? { width: '', height: '' } : { width: '1194', height: '370' },
                      };
                    if (selectedInch) {
                      const { width, height } = inchToDimensions[selectedInch] || { width: '', height: '' };
                      setDimensions({
                        width: {
                          input: width,
                          whole: width ? parseInt(width) : 0,
                          decimal: width && width.includes('.') ? parseInt(width.split('.')[1][0] || '0') : 0,
                        },
                        height: {
                          input: height,
                          whole: height ? parseInt(height) : 0,
                          decimal: height && height.includes('.') ? parseInt(height.split('.')[1][0] || '0') : 0,
                        },
                      });
                    } else {
                      setDimensions({
                        width: { input: '', whole: 0, decimal: 0 },
                        height: { input: '', whole: 0, decimal: 0 },
                      });
                    }
                    setSelectedInch(selectedInch);
                  }}
                  value={selectedInch}
                  disabled={productType === 'MNT' && !screenType}
                >
                  <option value="">Select inch size</option>
                  {productType === 'TV' && (
                    <>
                      <option value="32">32 inches</option>
                      <option value="40">40 inches</option>
                      <option value="42">42 inches</option>
                      <option value="43">43 inches</option>
                      <option value="48">48 inches</option>
                      <option value="50">50 inches</option>
                      <option value="55">55 inches</option>
                      <option value="58">58 inches</option>
                      <option value="65">65 inches</option>
                      <option value="70">70 inches</option>
                      <option value="75">75 inches</option>
                      <option value="77">77 inches</option>
                      <option value="85">85 inches</option>
                      <option value="86">86 inches</option>
                      <option value="98">98 inches</option>
                      <option value="100">100 inches</option>
                    </>
                  )}
                  {productType === 'MNT' && (
                    <>
                      <option value="16">16 inches</option>
                      <option value="22">22 inches</option>
                      <option value="24">24 inches</option>
                      <option value="25">25 inches</option>
                      <option value="27">27 inches</option>
                      <option value="30">30 inches</option>
                      <option value="32">32 inches</option>
                      <option value="34">34 inches</option>
                      <option value="45">45 inches</option>
                      <option value="49">49 inches</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        )}

        {['width', 'height'].map((dimension) => (
          <div key={dimension}>
            <label className="block mb-3 capitalize">
              {dimension}: {' '}
              <span className="text-gray-400">
                {dimensions[dimension].input ? `${dimensions[dimension].whole}.${dimensions[dimension].decimal} mm` : 'N/A'}
              </span>
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                value={dimensions[dimension].input}
                onChange={(e) => handleDimensionChange(dimension, e.target.value)}
                placeholder="Manually Input"
                className="bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400 w-40"
              />
            </div>
          </div>
        ))}

        <div className="flex space-x-4">
          <button 
            className={`px-6 py-3 rounded-lg mt-6 text-white w-full ${
              !isFormValid()
                ? "bg-gray-600 cursor-not-allowed" 
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductInfoInput;
